import { InboxEntry } from './inboxEntries';
import { MessagingPlatform } from './messagingPlatform.enum';
import { UserCommunication } from './userCommunication';

export interface WebSocketConnection {
  userId: string; // PK
  connectionId: string; // SK
  connectedAt: number;
  expiresAt: number;
}

export enum WebSocketAction {
  PING = 'PING',
  PONG = 'PONG',
  INSERT = 'INSERT',
  MODIFY = 'MODIFY',
  REMOVE = 'REMOVE',
  // New action, not a REMOVE shape change: older clients hit the default branch and ignore it.
  REMOVE_BATCH = 'REMOVE_BATCH',
  REPORT_BROWSER_RESULT = 'REPORT_BROWSER_RESULT',
}

export enum WebSocketEntityType {
  UserCommunication = 'UserCommunication',
  TaskGroup = 'TaskGroup',
  TaskLabel = 'TaskLabel',
  TaskSubgroup = 'TaskSubgroup',
  InboxEntry = 'InboxEntry',
  InboxEntryComment = 'InboxEntryComment',
  Notification = 'Notification',
  FlowGenerationJob = 'FlowGenerationJob',
  AgentEvent = 'AgentEvent',
  KnowledgeSpace = 'KnowledgeSpace',
  KnowledgePage = 'KnowledgePage',
  KnowledgeFavorite = 'KnowledgeFavorite',
  TeamInvite = 'TeamInvite',
  Team = 'Team',
  DoBoxRule = 'DoBoxRule',
  InboxEntryUnread = 'InboxEntryUnread',
  PersonActivity = 'PersonActivity',
  BrowserCommand = 'BrowserCommand',
  CalendarEvent = 'CalendarEvent',
  Person = 'Person',
  LocalDevicePresence = 'LocalDevicePresence',
  IntegrationAccount = 'IntegrationAccount',
  InboxEntryReplyState = 'InboxEntryReplyState',
}

/**
 * `threadMessageId` is the unit key (`InboxEntryNewReply.unitKey`). `threadHasUnread` is the
 * email head's mailbox state and is always false for chat.
 */
export interface InboxEntryReplyStatePayload {
  inboxEntryId: string;
  taskGroupId: string;
  threadMessageId: string;
  threadHasUnread: boolean;
  newReplyMessageIds: string[];
  computedAt: number;
}

export type InboxEntryViewFields = Pick<
  InboxEntry,
  | 'taskSubgroupId'
  | 'entryKind'
  | 'isSuggestion'
  | 'resolvedAt'
  | 'approvalDecision'
  | 'taskLabelIds'
  | 'participantIds'
  | 'priority'
  | 'dueAt'
>;

export interface WebSocketMessage<T> {
  action: WebSocketAction;
  timestamp?: number;
  entityType?: WebSocketEntityType;
  item?: T;
  // A MODIFY that cleared the item's resolvedAt. The item alone cannot say so,
  // and a client holding only part of the list cannot tell it from an update
  // to an entry it never loaded.
  reopened?: boolean;
  // A client holding part of the list needs the old values to tell an entry entering
  // its filtered view from one it never loaded.
  previous?: InboxEntryViewFields;
  // A MODIFY whose old and new images both carry deletedAt: a repeat write to a
  // deleted row, which a client counting deletes must not count again.
  alreadyDeleted?: boolean;
}

export interface PersonActivityMigratedPayload {
  personId: string;
}

export enum PersonActivityKind {
  COMMUNICATION = 'communication',
  EVENT = 'event',
}

export interface PersonActivityCreatedPayload {
  // Always user-scoped for communication — see "comunicação é privada" in design.md.
  personGlobalId: string;
  personId: string;
  kind: PersonActivityKind;
  createdAt: number;
  platform?: 'GMAIL' | 'OUTLOOK' | 'SLACK';
  // Lets the front recognize relevance when the open contact is the team copy.
  // The digest, not the address: this rides a socket and is matched against
  // Person.emailHmacs, which the API derives with the same key.
  counterpartEmailHmac?: string;
}

export type PersonActivityRemovedPayload =
  | {
      kind: PersonActivityKind.COMMUNICATION;
      personGlobalId: string;
      personId: string;
      // Raw identity — the front composes its list key via buildMessageActivityKey, not here.
      createdAt: number;
      messageId: string;
      counterpartEmailHmac?: string;
    }
  | {
      kind: PersonActivityKind.EVENT;
      personGlobalId: string;
      personId: string;
      eventId: string;
    };

export interface TeamPersonPayload {
  personGlobalId: string;
  personId: string;
  teamId: string;
  // The row's optimistic-lock version, sent on MODIFY — the web client uses it
  // to drop the echo of its own save without a refetch round-trip.
  version?: number;
  // Sent on the REMOVE a merge tombstone produces: the winner's personGlobalId,
  // already encoded (encodePersonRefGlobalId(buildPersonRef({kind:'team',teamId},
  // winnerPersonId))) so the client can move the viewer's selection to the
  // winner without decoding anything.
  mergedIntoGlobalId?: string;
}

// Minimal on purpose: $connect/$disconnect hold only the authorizer's ids, and making
// them assemble a full LocalDevice would add a table read to the relay's hot path.
// `userId` is redundant on the wire — the message only reaches its owner — but it is
// half of LocalDevice's Apollo cache key, so the client cannot identify the row without it.
export interface LocalDevicePresencePayload {
  userId: string;
  deviceId: string;
  isConnected: boolean;
  // $connect and $disconnect are independent invocations whose pushes can be in
  // flight at once; the client drops the older one per device. Carried on the item
  // because the event emitter forwards only `item`, not the message envelope.
  timestamp: number;
  mcpConnectionId?: string;
  lastSeenAt?: string;
}

export interface CalendarEventChangeNotification {
  accountEmail: string;
  platform: MessagingPlatform;
  calendarId?: string;
  eventId: string;
  changeType: string;
}

export interface FlowGenerationJobResult {
  jobId: string;
  status: string;
  mode?: string;
  flow?: Record<string, unknown>;
  group?: Record<string, unknown>;
  questions?: Array<{
    id: string;
    question: string;
    type: string;
    options?: string[];
    isOptional?: boolean;
  }>;
  upgradeSuggestion?: Record<string, unknown>;
  patch?: Record<string, unknown>;
  conversationMessages?: string;
  validationIssues?: Array<Record<string, unknown>>;
  error?: string;
  currentTool?: string;
}

export interface IntegrationAccountRemovedPayload {
  platform: MessagingPlatform;
  // The value rows carry in their platform filter attribute (userEmailAddress for
  // GMAIL / OUTLOOK / GOOGLE_CHAT). For TEAMS it is the disconnected account's homeAccountId.
  platformAccountId?: string;
}

// userEmailAddress is envelope-sealed (USER_COMMUNICATION_TARGET_FIELDS) and the REMOVE
// frame is built from a raw DynamoDB OldImage with no decryption, so it stays ciphertext.
export type UserCommunicationRemovePayload = Pick<
  UserCommunication,
  | 'userId'
  | 'messageId'
  | 'platform'
  | 'threadMessageId'
  | 'channel'
  | 'channelType'
  | 'labelIds'
  | 'draftId'
>;

export interface UserCommunicationRemoveBatchPayload {
  platform: MessagingPlatform;
  items: UserCommunicationRemovePayload[];
}
